import classNames from "classnames";
import { PdfAccessIndicator } from "components/PdfAccessIndicator";
import SearchResultActions from "components/SearchResultActions";
import ManageUploadedPaperDropdown from "features/ResearchHub/ManageUploadedPaperDropdown";
import { ResultFormat } from "components/Button/ResultFormatButton";
import { logDeleteUploadedPaperFromResearchHub } from "helpers/services/mixpanel";
import { PaperActionsProps } from "../../../types";

/**
 * @component PaperActions
 * @description Renders paper actions including save, cite, share, and manage buttons
 */
export const PaperActions = ({
  paper,
  text,
  textType,
  shareLink,
  index,
  showSaveBtn = false,
  showCiteBtn = false,
  showShareBtn = false,
  showManageUploadedPaperBtn = false,
  isLoadingExtractedAnswer = false,
  resultFormat,
  mixpanelActionSource,
  hashPaperRecordId,
  isUploading = false,
  isManageDropdownOpen = false,
  onManageDropdownOpenChange,
  onClickSave,
  onClickCite,
  onCitePaper,
  onFullTextClick,
  onSharePaper,
  className,
}: PaperActionsProps) => {
  const handleManageDropdownOpenChange = (open: boolean) => {
    onManageDropdownOpenChange?.(open);
  };

  // For table format, render actions in the table cell
  if (resultFormat === ResultFormat.TABLE) {
    return (
      <div className="flex justify-end mt-4 h-9">
        <SearchResultActions
          shareText={text}
          shareTextType={textType}
          shareLink={shareLink}
          item={paper}
          searchPage={0} //TODO(Jimmy): Pass Page to search result events here
          paperIndex={index}
          showShareBtn={showShareBtn && !isLoadingExtractedAnswer}
          showCiteBtn={showCiteBtn}
          showSaveBtn={showSaveBtn}
          mixpanelActionSource={mixpanelActionSource}
          onClickSave={onClickSave}
          onClickCite={onClickCite}
          onCitePaper={onCitePaper}
          onFullTextClick={onFullTextClick}
          sharePaperEventOverride={onSharePaper}
          className="w-full opacity-0 transition-opacity group-hover/row:opacity-100"
        />
      </div>
    );
  }

  return (
    <div
      className={classNames("flex flex-shrink-0 gap-2 items-center", className)}
    >
      {/* PDFAccessIndicator - always visible on small screens, toggles on large ones */}
      <div className="hidden group-hover:hidden @lg:flex">
        <PdfAccessIndicator paper={paper} />
      </div>
      {/* Actions button group for compact and non-compact results.
        On hover, the actions button group is shown for both compact and non-compact results.
        On mobile, the actions button group is shown for both compact and non-compact results.
      */}
      <div
        className={classNames(
          "flex overflow-hidden justify-end items-center transition-all duration-200",
          {
            "w-0 opacity-0 pointer-events-none @lg:w-0 @lg:opacity-0 @lg:group-hover:w-auto @lg:group-hover:opacity-100 @lg:group-hover:pointer-events-auto":
              !isManageDropdownOpen,
            "w-auto opacity-100 pointer-events-auto": isManageDropdownOpen,
          }
        )}
      >
        <SearchResultActions
          shareText={text}
          shareTextType={textType}
          shareLink={shareLink}
          item={paper}
          searchPage={0} //TODO(Jimmy): Pass Page to search result events here
          paperIndex={index}
          showShareBtn={showShareBtn && !isLoadingExtractedAnswer}
          showCiteBtn={showCiteBtn}
          showSaveBtn={showSaveBtn}
          mixpanelActionSource={mixpanelActionSource}
          onClickSave={onClickSave}
          onClickCite={onClickCite}
          onCitePaper={onCitePaper}
          onFullTextClick={onFullTextClick}
          sharePaperEventOverride={onSharePaper}
          noSocialSharing={resultFormat === ResultFormat.COMPACT}
        />

        {hashPaperRecordId && showManageUploadedPaperBtn && (
          <ManageUploadedPaperDropdown
            uploaded_paper_id={hashPaperRecordId}
            isUploading={isUploading}
            onDelete={() => {
              logDeleteUploadedPaperFromResearchHub(hashPaperRecordId);
            }}
            onOpenChange={handleManageDropdownOpenChange}
          />
        )}
      </div>
      {/* Actions dropdown for compact mobile results.  */}
      <div className="flex @lg:hidden">
        <SearchResultActions
          shareText={text}
          shareTextType={textType}
          shareLink={shareLink}
          item={paper}
          searchPage={0} //TODO(Jimmy): Pass Page to search result events here
          paperIndex={index}
          showShareBtn={showShareBtn && !isLoadingExtractedAnswer}
          showCiteBtn={showCiteBtn}
          showSaveBtn={showSaveBtn}
          mixpanelActionSource={mixpanelActionSource}
          onClickSave={onClickSave}
          onClickCite={onClickCite}
          onCitePaper={onCitePaper}
          onFullTextClick={onFullTextClick}
          sharePaperEventOverride={onSharePaper}
          noSocialSharing={resultFormat === ResultFormat.COMPACT}
          asDropdown={true}
        />
      </div>
    </div>
  );
};

export default PaperActions;
